import React, { useEffect, useMemo, useRef } from "react";
import TemporariesOverviewSearchBar from "@icoach/nonMember/temporary/TemporariesOverviewSearchBar";
import useOptions from "@util/hook/useOptions";
import useNonMemberApi from "@apis/useNonMemberApi";
import useTemporaryApi from "@apis/useTemporaryApi";
import { OverviewListLayout, OverviewListToolbar, PageSortAndTotalBox, OverviewListActionBar } from "@icoach/components/overviewList/";
import PageLayout from "@icoach/components/layout/PageLayout";
import OverflowMenu from "@icoach/components/overflowMenu/OverflowMenu";
import useOverview from "@util/hook/useOverview";
import { getTemporaryRowsLabel, menuTemporaryActions } from "@icoach/nonMember/nonMember.util";
import { TemporaryDeleteActionBar } from "@icoach/nonMember/components/";
import useCheckboxGroup from "@icoach/components/checkboxGroup/useCheckboxGroup";
import { isEmpty, isEqual } from "@util/utils";
import { FILTER_TYPE } from "@constants/";

/**
 * 預設搜尋參數
 * @type {Object}
 */
const defaultSearchParams = {
	pageIndex: 1,
	pageSize: 10,
	name: "", // 輸入框
	customerType: FILTER_TYPE.CUSTOMEROVERVIEW,
};

const TemporariesOverviewPage = ({ dialogRef: _dialogRef }) => {
	const overviewSearchBarRef = useRef(null);
	const tableRef = useRef(null);
	const { getTemorariesExcelApi } = useTemporaryApi();
	const { getInitTemporaryOptionsApi, postOverviewTemporaryApi } = useNonMemberApi();
	const { options } = useOptions({
		getOptionsApi: () => getInitTemporaryOptionsApi(),
		isFetchOnMount: true,
	});

	const { updateOverviewPage, searchParams, overviewData, getOverviewData } = useOverview({
		getOverviewApi: postOverviewTemporaryApi,
		defaultSearchParams: defaultSearchParams,
		enableInfiniteScroll: true,
	});

	const { getResult, Checkbox, CheckboxAll: CheckboxAllActionBar, onResetCheckBox, onUpdateCheckBox } = useCheckboxGroup({ tableRef });

	const { list, pagination } = overviewData || {};

	const listRef = useRef([]);

	// 刪除顧客 多選 / menu單選
	const handleDeleteData = target => {
		const ids = isEmpty(target) ? getResult() : target;
		const info = listRef.current.filter(({ temporaryID }) => ids.some(id => isEqual(id, temporaryID))).map(({ name, count }) => ({ name, count }));
		return {
			list: ids,
			info: info,
		};
	};

	const handleDeleteRefresh = () => {
		handleSubmit();
	};

	const handleSubmit = () => {
		const params = overviewSearchBarRef.current.getResult();
		params.pageIndex = 1;
		params.pageSize = 10;
		doOverviewApi(params);
	};

	const handlePageChange = (e, page) => {
		doPageChangeApi(page);
	};

	const handleExcelClick = tID => doTemporaryExcelApi(tID);

	const doTemporaryExcelApi = async tID => {
		await getTemorariesExcelApi(tID);
	};

	const doOverviewApi = async par => {
		await getOverviewData(par);
		onResetCheckBox();
	};

	const doPageChangeApi = async page => {
		await updateOverviewPage(page);
		onUpdateCheckBox();
	};

	const headerRow = useMemo(
		() => {
			const mAction = menuTemporaryActions({
				dialogRef: _dialogRef,
				onDeleteData: handleDeleteData,
				onDeleteRefresh: handleDeleteRefresh,
				onExcelClick: handleExcelClick,
			});
			const dropsMenu = [
				...getTemporaryRowsLabel(),
				{
					cellKey: "temporaryID",
					headerLabel: "",
					align: "center",
					formatCell: (cellValue, rowsData) => {
						return <OverflowMenu menuAction={mAction} sourceData={rowsData} />;
					},
				},
			];

			return dropsMenu;
		},
		// eslint-disable-next-line
		[],
	);

	// 盡量不要用，會不好理解和破壞render整體結構 by Allen
	useEffect(
		() => {
			listRef.current = list;
		},
		// eslint-disable-next-line
		[list],
	);

	return (
		<OverviewListLayout>
			<PageLayout.Header title="暫存名單" />
			<OverviewListLayout.SearchBar className={"mb-2"} onSubmit={handleSubmit}>
				<TemporariesOverviewSearchBar key={options.length} ref={overviewSearchBarRef} options={options} sourceData={searchParams} />
			</OverviewListLayout.SearchBar>
			<OverviewListToolbar>
				{list.length !== 0 && (
					<OverviewListActionBar>
						<CheckboxAllActionBar />
						<TemporaryDeleteActionBar getResult={handleDeleteData} dialogRef={_dialogRef} refresh={handleDeleteRefresh} />
					</OverviewListActionBar>
				)}
				<PageSortAndTotalBox pageIndex={pagination.pageIndex} pageSize={pagination.pageSize} totalCount={pagination.totalCount} isCurrentPageLastIndex />
			</OverviewListToolbar>
			<OverviewListLayout.Table
				headerRow={headerRow}
				list={list}
				totalPage={overviewData?.pagination?.totalPage}
				pageIndex={overviewData?.pagination?.pageIndex}
				isMasonryPagination
				onChangePage={handlePageChange}
				tableBodyProps={{ ref: tableRef }}
				tableRowBodyProps={{ Checkbox: Checkbox }}
			/>
		</OverviewListLayout>
	);
};

export default TemporariesOverviewPage;
